// AudioProcessor.js
export class AudioProcessor {
  constructor(room) {
    this.room = room; // Room object from LiveKit
    this.socket = null; // WebSocket connection (set dynamically)
    this.audioContext = null; // AudioContext instance
    this.workletNode = null; // AudioWorkletNode instance
    this.silentGain = null; // GainNode to keep graph alive without audible output
    this.isProcessing = false; // Indicates if audio processing is active
  }

  // Public Method: Start Audio Processing
  async start(socket) {
    if (this.isProcessing) {
      //console.log('Audio processing already started');
      return;
    }
    //console.log('Starting audio processing');
    if (!this.room || !this.room.localParticipant) {
      console.error('Room or local participant not available');
      return;
    }

    try {
      this.socket = socket; // Set the WebSocket reference dynamically
      await this.setupAudioContext();
      await this.waitForAudioTrack();
      this.startAudioProcessingLoop();
      this.isProcessing = true;
    } catch (error) {
      console.error('Error starting audio processing:', error);
      this.cleanup();
    }
  }

  // Public Method: Stop Audio Processing
  stop() {
    if (!this.isProcessing) {
     // //console.log('Audio processing already stopped');
      return;
    }
   // //console.log('Stopping audio processing');
    this.cleanup();
    this.isProcessing = false;
  }

  // Private Method: Set Up AudioContext and Load Worklet
  async setupAudioContext() {
    if (this.audioContext) {
     // //console.log('AudioContext already exists');
      return;
    }

    this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
      latencyHint: 'interactive',
    });

    ////console.log('Loading AudioWorklet module...');
    await this.audioContext.audioWorklet.addModule('/voice-processor.js');
    // Ensure the context is running (can be suspended until user gesture)
    if (this.audioContext.state === "suspended") {
      try {
        await this.audioContext.resume();
      } catch (e) {
        console.warn("[AudioProcessor] AudioContext resume failed:", e);
      }
    }

    // Retry mechanism for creating the AudioWorkletNode
    await this.createWorkletNode();
    this.workletNode.port.postMessage({ sampleRate: this.audioContext.sampleRate });
    this.workletNode.port.onmessage = (event) => {
      const outputData = event?.data?.buffer ?? event?.data;
      //console.log('audio worklet sending data');
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(outputData);
      }
    };

    // Keep the graph running without playing audio
    this.silentGain = this.audioContext.createGain();
    this.silentGain.gain.value = 0;
    this.workletNode.connect(this.silentGain);
    this.silentGain.connect(this.audioContext.destination);
  }

  // Recursive Retry Mechanism for Creating the WorkletNode
  async createWorkletNode(retryCount = 0) {
    try {
      this.workletNode = new AudioWorkletNode(this.audioContext, 'voice-processor');
      //console.log('AudioWorkletNode created successfully');
    } catch (error) {
      if (retryCount > 5) {
        throw error;
      }
      console.error(`Retrying AudioWorkletNode creation (${retryCount + 1}/5)...`);
      await new Promise((resolve) => setTimeout(resolve, 100));
      await this.createWorkletNode(retryCount + 1);
    }
  }

  // Private Method: Wait for Audio Track to Become Available
  async waitForAudioTrack() {
    const getAudioTrack = () => {
      return Array.from(this.room.localParticipant.audioTrackPublications.values())[0]?.track?.mediaStreamTrack;
    };

    let retryCount = 0;
    while (!getAudioTrack()) {
      if (retryCount > 10) {
        throw new Error('Audio track not available after multiple retries');
      }
      //console.log('Audio track not available, retrying in 1 second...');
      await new Promise((resolve) => setTimeout(resolve, 1000));
      retryCount++;
    }

    //console.log('Audio track detected');
  }

  // Private Method: Start Audio Processing Loop
  startAudioProcessingLoop() {
    const audioTrackPublication = Array.from(this.room.localParticipant.audioTrackPublications.values())[0];
    const mediaStream = new MediaStream([audioTrackPublication.track.mediaStreamTrack]);

    const source = this.audioContext.createMediaStreamSource(mediaStream);
    source.connect(this.workletNode);
    // Output path is already connected to a silent gain in setupAudioContext()

    //console.log('Audio processing loop started');
  }


  restart(socket){
    this.socket = socket; // Set the WebSocket reference dynamically
    this.stop()
    this.start(this.socket);
  }

  // Private Method: Clean Up Resources
  cleanup() {
    if (this.workletNode) {
      this.workletNode.disconnect();
      this.workletNode = null;
    }
    if (this.silentGain) {
      this.silentGain.disconnect();
      this.silentGain = null;
    }
    if (this.audioContext) {
      //console.log('Stopping audio processing');
      this.audioContext.close();
      this.audioContext = null;
    }
  }



}
